import { Button, DataDisplayEvent, Layout, Panel, SelectionEvent, Snackbar, TableColumn } from "@mcleod/components";
import {CityUtil, DateRange, DisplayType, DisplayValue, LogManager, ModelRow, StringUtil} from "@mcleod/core";
import { ExpandableFilterController } from "@mcleod/general";
import { AutogenLayoutEdiSuggestedPricing } from "./autogen/AutogenLayoutEdiSuggestedPricing";


const log = LogManager.getLogger("lme.datafusion.EdiSuggestedPricing");

export class EdiSuggestedPricing extends AutogenLayoutEdiSuggestedPricing {
    private expandableFilter: ExpandableFilterController = new ExpandableFilterController();
    private isLaneHistoryDataLoaded: boolean = false;
    private isRateIndexDataLoaded: boolean = false;
    private isCommitmentDataLoaded: boolean = false;
    private initialSelectedTabId: string;
    private excludeButtonOnClick: () => {};
    private declineButtonOnClick: () => {};
    private acceptButtonOnClick: () => {};

    onLoad() {
        this.setupExpandableFilter();
        this.layoutTenderRates.panelOtherChargeTable.collapse();
        this.layoutLaneHistory.tableLaneHistory.addColumn(new TableColumn({ headingDef: { caption: "Revenue Code" }, cell: { paddingLeft: 20, field: "orders.revenue_code_id" } }), true, false);
        this.sourceRateIndexControl.search().then((response) => {
            const data = response["modelRows"];
            const firstSetupVendor = data.find(item => item?.data?.licensed_and_setup);
            this.tabRateIndex.visible =  !!firstSetupVendor;
        });
        this.textboxStopsPickDrop.quickInfoOverrideSearchMethod = async (layout: Layout, _keyValue: any) => {
            await layout.mainDataSource?.search({ order_id: this.activeRow?.get("id")});
        };
    }

    async sourceEdiOrdersListOnDisplay(event: DataDisplayEvent) {
        switch (this.initialSelectedTabId) {
            case "tabLaneHistory":
                this.searchLaneHistoryFilter();
                this.tabsetEdiSuggestedPricing.selectedIndex = this.tabLaneHistory._index;
                break;
            case "tabRateIndex":
                const rateRetrievalVendorList = await this.setAutoRetrievalVendorList();
                if (rateRetrievalVendorList.length > 0) {
                    await this.populateRateInformation(rateRetrievalVendorList);
                } else {
                    this.populateRateIndexInformation();
                }
                this.tabsetEdiSuggestedPricing.selectedIndex = this.tabRateIndex._index;
                break;
            case "tabCommitmentAutoRating":
                this.populateCommitmentInformation();
                this.tabsetEdiSuggestedPricing.selectedIndex = this.tabCommitmentAutoRating._index;
                break;
        }
    }

    async tabsetEdiSuggestedPricingBeforeTabSelection(event: SelectionEvent) {
        if (!this.isLaneHistoryDataLoaded && event.newSelection.element.id === "tabLaneHistory") {
            this.searchLaneHistoryFilter();
            this.isLaneHistoryDataLoaded = true;
        }
        else if (!this.isRateIndexDataLoaded && event.newSelection.element.id === "tabRateIndex") {
            const rateRetrievalVendorList = await this.setAutoRetrievalVendorList();
            if (rateRetrievalVendorList.length > 0) {
                await this.populateRateInformation(rateRetrievalVendorList);
            } else {
                this.populateRateIndexInformation();
            }
        }
        else if (!this.isCommitmentDataLoaded && event.newSelection.element.id === "tabCommitmentAutoRating")
            this.populateCommitmentInformation();
    }

    populateCommitmentInformation() {
        const row = this.mainDataSource.activeRow;
        if (row) {
            const origDestRateId = row?.get("orig_dest_rate_id");
            const commitmentId = row?.get("commitment_id");
            const filter = commitmentId ? { id: origDestRateId, "commitment_detail.id": commitmentId } : { id: origDestRateId };
            if (origDestRateId) {
                this.layoutCommitmentAutoRating.search(filter);
            }
            if (!commitmentId) {
                Snackbar.showSnackbar("No active commitment detail found. Load tender did not receive a commitment ID when auto rated.");
            }
            this.isCommitmentDataLoaded = true;
        }
    }

    async populateRateIndexInformation() {
        const rateIndexSnackbar = Snackbar.showDownloadSnackbar("Loading...", "Loading rate index information...");
        this.layoutRateIndex.setDefaultValues();
        this.layoutRateIndex.hideColumns();
        this.layoutRateIndex.buttonFetchOtherResults.visible = false;
        this.layoutRateIndex.marginBottom = 20;
        await this.sourceEdiStop?.search({ order_id: this.activeRow?.get("id") }).then(response => {
            if (response) {
                const stopData = response["modelRows"].map(value => value.data);
                this.layoutRateIndex.search({
                    stops: stopData,
                    mileage: this.activeRow?.get("bill_distance"),
                    equipment_id: this.activeRow?.get("equipment_type_id"),
                    show_all_total_rates: true
                }).then((response) => {
                    if (response) {
                        const data = response["modelRows"][0].data;
                        for (const vendorData of data["vendors_data"]) {
                            this.layoutRateIndex.populateRateIndexResultData(vendorData, false, data["show_company_scac"], null);
                        }
                    }
                    else log.error("Error fetching rate index results for Load Tender Express");
                    rateIndexSnackbar.dismiss(0, true);
                });
            }
        });
        this.isRateIndexDataLoaded = true;
    }

    // START - Expandable Filter Logic
    private setupExpandableFilter() {
        const panelSearchHeader = this.tabLaneHistory.findComponentById("panelSearchHeader") as Panel;
        const panelSearchFilter = this.tabLaneHistory.findComponentById("panelSearchFilter") as Panel;
        this.expandableFilter.initialize(panelSearchHeader, panelSearchFilter);
        this.expandableFilter.toggle();
        this.expandableFilter.onClear = (event) => this.clearLaneHistoryFilter();
        this.expandableFilter.onSearch = (event) => this.searchLaneHistoryFilter();
    }

    private clearLaneHistoryFilter() {
        this.sourceLaneHistory.searchRow.clear();
        this.textboxRadius.clear();
        this.textboxDateRange.clear();
        this.textboxMovementStatus.clear();
        this.textboxMovementStatus.selectedItem = null;
        this.textboxEquipmentType.clear();
        this.textboxRevenueCode.clear();
    }

    private searchLaneHistoryFilter() {
        const data = this.mainDataSource.activeRow;
        const findNearCityId = data?.get("edi_stop_origin.city_id");
        const destinationState = data?.get("edi_stop_consignee.state");
        this.textboxRadius.text = "75";
        this.textboxRevenueCode.displayComponentData(data, null, 0);
        this.textboxEquipmentType.displayComponentData(data, null, 0);
        this.citystateStopOriginCityId.textCombined.text = CityUtil.formatCityStateZip(data?.get("edi_stop_origin.city_name"), data?.get("edi_stop_origin.state"), data?.get("edi_stop_origin.zip_code"));

        const filter: ModelRow = this.sourceLaneHistory.getSearchFilter();
        if (filter.isNull("stop_origin.sched_arrive_early")) {
            const dateRange = DateRange.parseDateRangeWithKeywords("t-180,t");
            this.textboxDateRange.text = DisplayValue.getDisplayValue(dateRange, DisplayType.DATERANGE, this.textboxDateRange.format);
        }
        if (filter.isNull("find_near_radius")) {
            this.textboxRadius.text = "75";
            filter.set("find_near_radius", 75)
        }
        filter.setValues({
            find_near_city_id: findNearCityId,
            destination_state: destinationState
        });
        if (findNearCityId && destinationState) this.layoutLaneHistory.search(filter.data);
    }
    // END - Expandable Filter Logic

    get excludeButton(): Button {
        return new Button({
            id: "excludeButton",
            caption: "Exclude",
            backgroundColor: "defaultBackground",
            color: "primary",
            fontBold: true,
            minWidth: 128,
            borderWidth: 0,
            rowBreak: false,
            onClick: () => {
                this.excludeButtonOnClick();
            }
        });
    }

    get declineButton(): Button {
        return new Button({
            id: "declineButton",
            caption: "Decline",
            backgroundColor: "defaultBackground",
            color: "primary",
            minWidth: 128,
            fontBold: true,
            borderWidth: 0,
            rowBreak: false,
            onClick: () => {
                this.declineButtonOnClick();
            }
        });
    }

    get acceptButton(): Button {
        return new Button({
            id: "acceptButton",
            caption: "Accept",
            backgroundColor: "primary",
            color: "McLeodWhite",
            minWidth: 128,
            fontBold: true,
            borderWidth: 0,
            rowBreak: false,
            onClick: () => {
                this.acceptButtonOnClick();
            }
        });
    }

    set excludeButtonCallback(callback) {
        this.excludeButtonOnClick = callback;
    }

    set declineButtonCallback(callback) {
        this.declineButtonOnClick = callback;
    }

    set acceptButtonCallback(callback) {
        this.acceptButtonOnClick = callback;
    }

    set initialSelectedTab(id: string) {
        this.initialSelectedTabId = id;
    }

    private async setAutoRetrievalVendorList() {
        const rateRetrievalVendorList = [];
        await this.sourceRateIndexControl.search().then(data => {
            if (data && data.modelRows) {
                data.modelRows.forEach(row => {
                    if (!StringUtil.isEmptyString(row?._data?.auto_rate_retrieval) && StringUtil.equalsIgnoreCase(row?._data?.auto_rate_retrieval, "Y")) {
                        rateRetrievalVendorList.push(row?._data.vendor);
                    }
                })
            }
        });
        return rateRetrievalVendorList;
    }

    private async populateRateInformation(rateRetrievalVendorList: any[]) {
        const rateIndexSnackbar = Snackbar.showDownloadSnackbar("Loading...", "Loading rate index information...");
        this.layoutRateIndex.setDefaultValues();
        this.layoutRateIndex.hideColumns();
        this.layoutRateIndex.buttonFetchOtherResults.visible = false;
        this.layoutRateIndex.marginBottom = 20;

        try {
            const stopResponse = await this.sourceEdiStop?.search({ order_id: this.activeRow?.get("id") });
            if (!stopResponse) {
                Snackbar.showSnackbar("Error fetching stop data");
                return;
            }

            const stopData = stopResponse["modelRows"].map(value => value.data);
            const mileage = this.activeRow?.get("bill_distance");
            const equipmentId = this.activeRow?.get("equipment_type_id");

            // Process each vendor sequentially to maintain order
            for (const vendor of rateRetrievalVendorList) {
                try {
                    const searchParams = {
                        stops: stopData,
                        mileage: mileage,
                        equipment_id: equipmentId,
                        vendor: vendor,
                        show_all_total_rates: true
                    };

                    const response = await this.layoutRateIndex.search(searchParams);
                    if (response?.modelRows?.[0]?.data) {
                        const data = response.modelRows[0].data;
                        if (data.vendors_data?.length > 0) {
                            data.vendors_data.forEach(vendorData => {
                                this.layoutRateIndex.populateRateIndexResultData(
                                    vendorData,
                                    false,
                                    data.show_company_scac,
                                    null
                                );
                            });
                        } else {
                            Snackbar.showSnackbar(`Error fetching rate index results for vendor: ` + this.getVendorName(vendor));
                        }
                    } else {
                        Snackbar.showSnackbar(`Invalid response format for vendor  ` + this.getVendorName(vendor));
                    }
                } catch (error) {
                    Snackbar.showSnackbar(`Error processing vendor: ${this.getVendorName(vendor)}`);
                }
            }
        } catch (error) {
            log.error(`Unexpected error during rate retrieval: ${error}`);
            Snackbar.showSnackbar("Unexpected error during rate retrieval.");
        } finally {
            rateIndexSnackbar.dismiss(0, true);
            this.isRateIndexDataLoaded = true;
        }
    }

    private getVendorName(vendor: string): string {
        switch (vendor.toUpperCase()) {
            case "T":
                return "DAT";
            case "I":
                return "Internet Truckstop";
            case "E":
                return "McLeod Exchange";
            case "F":
                return "SONAR TRAC";
            case "G":
                return "Greenscreens";
            default:
                return "";
        }
    }
}
